<script lang="ts" setup>
import type { Page } from '@autobid/strapi-integration/typescript/strapi/Page'
import { useStrapiLang } from '@autobid/strapi-integration/composable/useStrapiLang'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { formatDate } from '@autobid/ui/utils/date/formatDate'
import { useQuery } from '@tanstack/vue-query'

interface Props {
  page: Page
}

defineProps<Props>()

const {
  VERSION,
  autobidCMS: { url, singularCollection },
} = useRuntimeConfig().public
const { strapiLang: lang } = useStrapiLang()
const { $trpc } = useNuxtApp()
const push = usePush()

const { data, isLoading } = useQuery({
  queryKey: ['apiVersion'],

  queryFn: async () => {
    try {
      return await $trpc.auctions.getAuctionApiVersion.query()
    }
    catch (error) {
      const { message, handled } = getError(error)

      if (!handled) {
        push.error(message)
      }

      return null
    }
  },
})
</script>

<template>
  <div
    class="flex max-h-[85vh] w-full flex-col items-center justify-between overflow-y-auto px-3 md:flex-row md:flex-wrap md:overflow-hidden"
  >
    <div class="flex items-center justify-between">
      <div class="admin-nav flex flex-col flex-wrap items-center md:flex-row">
        <a
          :href="`${url}/admin/content-manager/collection-types/api::${singularCollection}.${singularCollection}/${page.documentId}?plugins[i18n][locale]=${lang}`"
          target="_blank"
          class="hover:underline"
        >
          {{ $t('admin-nav.edit-page') }}
        </a>
        <span v-if="page.updatedAt || page.createdAt">
          {{ $t('admin-nav.updated-at') }}
          {{ formatDate(String(page.updatedAt || page.createdAt)) }}
        </span>
      </div>
    </div>
    <div>
      <CommonTooltip
        tooltip="Version of the application"
        class="inline-block cursor-default"
      >
        APP v.{{ VERSION }}
      </CommonTooltip>
      <span v-if="data">&nbsp;|&nbsp;</span>
      <CommonTooltip
        v-if="!isLoading && data"
        tooltip="Version of the Auctions API"
        class="inline-block cursor-default"
      >
        API v.{{ data.branch }}
      </CommonTooltip>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.admin-nav {
  & > * {
    @media (min-width: 768px) {
      border-right: 1px solid #fff;
      padding-right: 10px;
      margin-right: 10px;
    }

    @media (max-width: 767px) {
      padding: 4px 0;
    }
  }
}
</style>
